import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  UserX, Plus, Search, Loader2, Phone, Mail, MapPin,
  Trash2, Edit2, X, Building2, Globe, Briefcase
} from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';

// US States list
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 
  'Wisconsin', 'Wyoming'
];

export default function OpposingCounselPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [counsels, setCounsels] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddCounsel, setShowAddCounsel] = useState(false);
  const [editingCounsel, setEditingCounsel] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const emptyCounsel = {
    first_name: '',
    last_name: '',
    law_firm: '',
    email: '',
    phone: '',
    website: '',
    address: {
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: 'California',
      zip: ''
    }
  };
  
  const [counselForm, setCounselForm] = useState(emptyCounsel);

  useEffect(() => {
    const initData = async () => {
      await fetchUser();
      await fetchCounsels();
    };
    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        
        if (userData.app_mode !== 'crm') {
          toast.error('CRM features require CRM subscription');
          navigate('/dashboard');
        }
      } else {
        navigate('/auth');
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchCounsels = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/crm/opposing-counsel`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCounsels(data);
      }
    } catch (error) {
      console.error('Error fetching opposing counsels:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCounsel = async () => {
    if (!counselForm.first_name.trim() || !counselForm.last_name.trim()) {
      toast.error('First name and last name are required');
      return;
    }
    
    setSaving(true);
    const token = localStorage.getItem('token');
    
    try {
      const url = editingCounsel 
        ? `${API}/crm/opposing-counsel/${editingCounsel.counsel_id}`
        : `${API}/crm/opposing-counsel`;
      
      const response = await fetch(url, {
        method: editingCounsel ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(counselForm)
      });
      
      if (response.ok) {
        toast.success(editingCounsel ? 'Opposing counsel updated' : 'Opposing counsel created');
        setShowAddCounsel(false);
        setEditingCounsel(null);
        setCounselForm(emptyCounsel);
        fetchCounsels();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save opposing counsel');
      }
    } catch (error) {
      toast.error('Failed to save opposing counsel');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCounsel = async (counselId) => {
    if (!confirm('Are you sure you want to delete this opposing counsel?')) return;
    
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API}/crm/opposing-counsel/${counselId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Opposing counsel deleted');
        fetchCounsels();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete opposing counsel');
      }
    } catch (error) {
      toast.error('Failed to delete opposing counsel');
    }
  };

  const handleEditCounsel = (counsel) => {
    setCounselForm({
      first_name: counsel.first_name || '',
      last_name: counsel.last_name || '',
      law_firm: counsel.law_firm || '',
      email: counsel.email || '',
      phone: counsel.phone || '',
      website: counsel.website || '',
      address: counsel.address || emptyCounsel.address
    });
    setEditingCounsel(counsel);
    setShowAddCounsel(true);
  };

  const filteredCounsels = counsels.filter(counsel => {
    const fullName = `${counsel.first_name} ${counsel.last_name}`.toLowerCase();
    const search = searchQuery.toLowerCase();
    return fullName.includes(search) ||
      counsel.law_firm?.toLowerCase().includes(search) ||
      counsel.email?.toLowerCase().includes(search) ||
      counsel.phone?.includes(search);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <Sidebar user={user} />
      
      <div className="flex-1 flex flex-col lg:ml-64">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-6 py-8 lg:pt-8 pt-20">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Opposing Counsel</h1>
            <p className="text-slate-500">{counsels.length} counsel{counsels.length !== 1 ? 's' : ''} in directory</p>
          </div>
          <Button
            onClick={() => {
              setCounselForm(emptyCounsel);
              setEditingCounsel(null);
              setShowAddCounsel(true);
            }}
            className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl"
            data-testid="add-counsel-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Opposing Counsel
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, firm, email, or phone..."
            className="pl-12 h-12 rounded-xl bg-white"
            data-testid="search-counsels"
          />
        </div>

        {/* Counsel Table */}
        {filteredCounsels.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <UserX className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              {searchQuery ? 'No opposing counsel found' : 'No opposing counsel yet'}
            </h3>
            <p className="text-slate-500 mb-6">
              {searchQuery 
                ? 'Try a different search term' 
                : 'Add your first opposing counsel to get started'}
            </p>
            {!searchQuery && (
              <Button
                onClick={() => setShowAddCounsel(true)}
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Opposing Counsel
              </Button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="counsels-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Name</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Law Firm</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Contact</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Location</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Added</th>
                    <th className="text-right px-6 py-4 text-sm font-semibold text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredCounsels.map((counsel) => (
                    <tr
                      key={counsel.counsel_id}
                      className="hover:bg-slate-50 transition-colors"
                      data-testid={`counsel-row-${counsel.counsel_id}`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-white font-semibold text-sm">
                            {counsel.first_name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{counsel.first_name} {counsel.last_name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {counsel.law_firm ? (
                          <p className="text-sm text-slate-600 flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            {counsel.law_firm}
                          </p>
                        ) : (
                          <span className="text-sm text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          {counsel.email && (
                            <p className="text-sm text-slate-600 flex items-center gap-2">
                              <Mail className="w-4 h-4 text-slate-400" />
                              {counsel.email}
                            </p>
                          )}
                          {counsel.phone && (
                            <p className="text-sm text-slate-600 flex items-center gap-2">
                              <Phone className="w-4 h-4 text-slate-400" />
                              {counsel.phone}
                            </p>
                          )}
                          {counsel.website && (
                            <p className="text-sm text-slate-600 flex items-center gap-2">
                              <Globe className="w-4 h-4 text-slate-400" />
                              <a href={counsel.website} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline">
                                Website
                              </a>
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {counsel.address?.city ? (
                          <p className="text-sm text-slate-600 flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-slate-400" />
                            {counsel.address.city}, {counsel.address.state}
                          </p>
                        ) : (
                          <span className="text-sm text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-slate-600">
                          {new Date(counsel.created_at).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditCounsel(counsel)}
                            className="rounded-lg h-8 w-8 p-0 hover:bg-violet-50 hover:text-violet-700"
                            data-testid={`edit-counsel-btn-${counsel.counsel_id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCounsel(counsel.counsel_id)}
                            className="rounded-lg h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-counsel-btn-${counsel.counsel_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
          </div>
        </div>
      </div>

      {/* Add/Edit Counsel Modal */}
      {showAddCounsel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
              <h2 className="text-xl font-semibold">
                {editingCounsel ? 'Edit Opposing Counsel' : 'Add New Opposing Counsel'}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setShowAddCounsel(false); setEditingCounsel(null); }}
                className="rounded-lg"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Basic Information
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-slate-600">First Name *</Label>
                      <Input
                        value={counselForm.first_name}
                        onChange={(e) => setCounselForm({ ...counselForm, first_name: e.target.value })}
                        placeholder="John"
                        className="mt-1"
                        data-testid="counsel-firstname-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Last Name *</Label>
                      <Input
                        value={counselForm.last_name}
                        onChange={(e) => setCounselForm({ ...counselForm, last_name: e.target.value })}
                        placeholder="Smith"
                        className="mt-1"
                        data-testid="counsel-lastname-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Law Firm</Label>
                    <Input
                      value={counselForm.law_firm}
                      onChange={(e) => setCounselForm({ ...counselForm, law_firm: e.target.value })}
                      placeholder="Smith & Associates"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Contact Information
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-slate-600">Email</Label>
                      <Input
                        value={counselForm.email}
                        onChange={(e) => setCounselForm({ ...counselForm, email: e.target.value })}
                        placeholder="john@smithlaw.com"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Phone</Label>
                      <Input
                        value={counselForm.phone}
                        onChange={(e) => setCounselForm({ ...counselForm, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Website</Label>
                    <Input
                      value={counselForm.website}
                      onChange={(e) => setCounselForm({ ...counselForm, website: e.target.value })}
                      placeholder="https://smithlaw.com"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Address
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm text-slate-600">Address Line 1</Label>
                    <Input
                      value={counselForm.address.address_line_1}
                      onChange={(e) => setCounselForm({ 
                        ...counselForm, 
                        address: { ...counselForm.address, address_line_1: e.target.value }
                      })}
                      placeholder="123 Main Street"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Address Line 2</Label>
                    <Input
                      value={counselForm.address.address_line_2}
                      onChange={(e) => setCounselForm({ 
                        ...counselForm, 
                        address: { ...counselForm.address, address_line_2: e.target.value }
                      })}
                      placeholder="Suite 100"
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm text-slate-600">City</Label>
                      <Input
                        value={counselForm.address.city}
                        onChange={(e) => setCounselForm({ 
                          ...counselForm, 
                          address: { ...counselForm.address, city: e.target.value }
                        })}
                        placeholder="Los Angeles"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">State</Label>
                      <select
                        value={counselForm.address.state}
                        onChange={(e) => setCounselForm({ 
                          ...counselForm, 
                          address: { ...counselForm.address, state: e.target.value }
                        })}
                        className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white"
                      >
                        {US_STATES.map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">ZIP</Label>
                      <Input
                        value={counselForm.address.zip}
                        onChange={(e) => setCounselForm({ 
                          ...counselForm, 
                          address: { ...counselForm.address, zip: e.target.value }
                        })}
                        placeholder="90001"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3 sticky bottom-0 bg-white rounded-b-2xl">
              <Button
                variant="outline"
                onClick={() => { setShowAddCounsel(false); setEditingCounsel(null); }}
                className="rounded-xl"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveCounsel}
                disabled={saving}
                className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="save-counsel-btn"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</>
                ) : (
                  editingCounsel ? 'Update Opposing Counsel' : 'Create Opposing Counsel'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
